/**
 * Image Style Select Component
 *
 * A thumbnail grid picker for selecting AI image generation styles.
 * Displays preview images with style name overlays in a responsive grid,
 * plus an "Auto" option with a sparkle icon for AI-driven selection.
 *
 * Handles missing preview images gracefully by showing the style name
 * on a neutral background as fallback.
 */

import React, { useState } from 'react';
import { Wand2 } from 'lucide-react';


/**
 * Dynamically imports all image style preview images that exist on disk.
 * Uses import.meta.glob so missing files are silently omitted rather than
 * causing a hard build failure (ENOENT). The resulting map is keyed by the
 * full module path; we extract the filename stem to build a value -> URL lookup.
 */
const imageModules = import.meta.glob<string>(
  '@/assets/images/image-styles/*.{png,jpg,jpeg,webp}',
  { eager: true, import: 'default' },
);

/** Maps image style enum values (e.g. "watercolor") to their resolved asset URLs */
const IMAGE_STYLE_PREVIEWS: Record<string, string> = Object.fromEntries(
  Object.entries(imageModules).map(([path, url]) => {
    const filename = path.split('/').pop()?.replace(/\.[^.]+$/, '') ?? '';
    return [filename, url];
  }),
);

export interface ImageStyleSelectProps {
  /** Current selected value ("auto" or an ImageStyle enum value) */
  value: string;
  /** Callback when a style is selected */
  onChange: (value: string) => void;
  /** Available image style options from reference data */
  options: Array<{ value: string; description: string; label?: string }>;
  /** Whether the selector is disabled */
  disabled?: boolean;
  /** Additional CSS classes for the container */
  className?: string;
  /** Label for the Auto card */
  autoLabel?: string;
  /** Tooltip description for the Auto card */
  autoDescription?: string;
}

/** Formats an enum value into a human-readable display label */
const formatStyleLabel = (value: string): string => {
  return value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const ImageStyleSelect: React.FC<ImageStyleSelectProps> = ({
  value,
  onChange,
  options,
  disabled = false,
  className = '',
  autoLabel = 'Auto',
  autoDescription = 'Let the AI choose the best style for this content',
}) => {
  /** Tracks which images failed to load, enabling fallback rendering */
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (styleValue: string) => {
    setFailedImages((prev) => new Set(prev).add(styleValue));
  };

  const isAutoSelected = value === 'auto';

  return (
    <div className={`flex gap-4 ${className}`}>
      {/* Left: 5-column x 2-row image grid */}
      <div className="flex-1 grid grid-cols-5 gap-2">
        {options.map((option) => {
          const isSelected = value === option.value;
          const previewSrc = IMAGE_STYLE_PREVIEWS[option.value];
          const hasImage = previewSrc && !failedImages.has(option.value);
          const label = option.label || formatStyleLabel(option.value);

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => !disabled && onChange(option.value)}
              disabled={disabled}
              data-card-value={option.value}
              title={option.description}
              className={`
                relative overflow-hidden rounded-lg border-2
                transition-all duration-150 cursor-pointer
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] hover:border-theme-border-strong'}
                ${
                  isSelected
                    ? 'border-theme-accent-primary shadow-sm'
                    : 'border-theme-border-medium'
                }
              `}
              aria-pressed={isSelected}
            >
              {hasImage ? (
                <>
                  <img
                    src={previewSrc}
                    alt={label}
                    className="w-full aspect-[4/3] object-cover"
                    onError={() => handleImageError(option.value)}
                  />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <span className="text-[11px] font-medium text-white drop-shadow-sm">
                      {label}
                    </span>
                  </div>
                </>
              ) : (
                <div className="w-full aspect-[4/3] flex items-center justify-center bg-theme-card p-2">
                  <span
                    className={`text-xs font-medium text-center leading-tight ${
                      isSelected ? 'text-theme-accent-primary' : 'text-theme-text-secondary'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              )}

              {isSelected && (
                <div className="absolute inset-0 rounded-md ring-1 ring-inset ring-theme-accent-primary/20" />
              )}
            </button>
          );
        })}
      </div>

      {/* Right: Auto card */}
      <button
        type="button"
        onClick={() => !disabled && onChange('auto')}
        disabled={disabled}
        data-card-value="auto"
        title={autoDescription}
        className={`
          flex-shrink-0 w-24 px-2 flex flex-col items-center justify-center rounded-lg border-2
          transition-all duration-150 cursor-pointer self-stretch
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02] hover:border-theme-border-strong'}
          ${
            isAutoSelected
              ? 'border-theme-accent-primary shadow-sm'
              : 'border-theme-border-medium bg-theme-card'
          }
        `}
        style={
          isAutoSelected
            ? { backgroundColor: 'rgba(var(--theme-accent-primary-rgb, 19, 73, 173), 0.08)' }
            : undefined
        }
        aria-pressed={isAutoSelected}
      >
        <Wand2
          className={`w-8 h-8 mb-2 ${
            isAutoSelected ? 'text-theme-accent-primary' : 'text-theme-text-tertiary'
          }`}
        />
        <span
          className={`text-sm font-medium text-center leading-tight ${
            isAutoSelected ? 'text-theme-accent-primary' : 'text-theme-text-secondary'
          }`}
        >
          {autoLabel}
        </span>
      </button>
    </div>
  );
};
